/**
 * Keyboard Shortcuts System
 *
 * Provides global keyboard shortcuts with support for:
 * - Single key shortcuts (?, /)
 * - Modifier combinations (Cmd+K, Ctrl+Shift+P)
 * - Sequential shortcuts (G then D for "Go to Dashboard")
 * - Automatic OS detection for ⌘ vs Ctrl display
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface Shortcut {
  /** Unique identifier for the shortcut */
  id: string;
  /** The key(s) to press. For sequences, use array: ['g', 'd'] */
  keys: string | string[];
  /** Require Cmd (Mac) or Ctrl (Windows/Linux) */
  meta?: boolean;
  /** Require Shift */
  shift?: boolean;
  /** Require Alt/Option */
  alt?: boolean;
  /** Action to perform */
  action: () => void;
  /** Human-readable description */
  description: string;
  /** Category for grouping in shortcuts dialog */
  category?: 'navigation' | 'actions' | 'editing' | 'general';
  /** Whether the shortcut is currently enabled */
  enabled?: boolean;
}

export interface ShortcutGroup {
  category: string;
  shortcuts: Shortcut[];
}

// =============================================================================
// Platform Detection
// =============================================================================

/** Check if running on macOS */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/** Get the modifier key symbol for the current platform */
export function getModifierSymbol(): string {
  return isMac() ? '⌘' : 'Ctrl';
}

/** Format shortcut keys for display */
export function formatShortcut(shortcut: Shortcut): string {
  const parts: string[] = [];

  if (shortcut.meta) parts.push(getModifierSymbol());
  if (shortcut.shift) parts.push('⇧');
  if (shortcut.alt) parts.push(isMac() ? '⌥' : 'Alt');

  const keys = Array.isArray(shortcut.keys) ? shortcut.keys : [shortcut.keys];
  parts.push(...keys.map((k) => k.toUpperCase()));

  return parts.join(Array.isArray(shortcut.keys) ? ' then ' : '+');
}

// =============================================================================
// Hook: useKeyboardShortcuts
// =============================================================================

interface UseKeyboardShortcutsOptions {
  /** Shortcuts to register */
  shortcuts: Shortcut[];
  /** Whether shortcuts are globally enabled */
  enabled?: boolean;
  /** Timeout for sequential shortcuts (ms) */
  sequenceTimeout?: number;
}

/**
 * Hook for registering keyboard shortcuts.
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     { id: 'search', keys: 'k', meta: true, action: openSearch, description: 'Open search' },
 *     { id: 'goto-dashboard', keys: ['g', 'd'], action: () => navigate('/'), description: 'Go to dashboard' },
 *   ],
 * })
 * ```
 */
export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
  sequenceTimeout = 1000,
}: UseKeyboardShortcutsOptions) {
  const sequenceRef = useRef<string[]>([]);
  const sequenceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs. A checkbox or radio takes no text,
      // so a key pressed after ticking one is still meant for the page.
      const target = e.target as HTMLElement;
      const isTextlessInput =
        target instanceof HTMLInputElement &&
        ['checkbox', 'radio', 'button', 'submit', 'reset'].includes(target.type);
      const isInput =
        (target.tagName === 'INPUT' && !isTextlessInput) ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.getAttribute('role') === 'textbox' ||
        target.closest('[contenteditable="true"]') !== null;

      // Allow Escape and some meta shortcuts even in inputs
      const allowInInput = e.key === 'Escape' || e.metaKey || e.ctrlKey;
      if (isInput && !allowInInput) return;

      const key = e.key.toLowerCase();

      for (const shortcut of shortcuts) {
        if (shortcut.enabled === false) continue;

        const keys = Array.isArray(shortcut.keys) ? shortcut.keys : [shortcut.keys];
        const isSequence = keys.length > 1;

        // Check modifier requirements
        const metaOrCtrl = e.metaKey || e.ctrlKey;
        const metaMatch = shortcut.meta ? metaOrCtrl : !metaOrCtrl;
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
        const altMatch = shortcut.alt ? e.altKey : !e.altKey;

        if (isSequence) {
          // Handle sequential shortcuts (e.g., G then D)
          // Only process if no modifiers required
          if (shortcut.meta || shortcut.shift || shortcut.alt) continue;

          // Add to sequence
          sequenceRef.current.push(key);

          // Clear sequence after timeout
          clearTimeout(sequenceTimerRef.current);
          sequenceTimerRef.current = setTimeout(() => {
            sequenceRef.current = [];
          }, sequenceTimeout);

          // Check if sequence matches
          const sequenceStr = sequenceRef.current.join(',');
          const targetStr = keys.map((k) => k.toLowerCase()).join(',');

          if (sequenceStr === targetStr) {
            e.preventDefault();
            sequenceRef.current = [];
            clearTimeout(sequenceTimerRef.current);
            shortcut.action();
            return;
          }
        } else {
          // Handle single-key shortcuts
          if (keys[0].toLowerCase() === key && metaMatch && shiftMatch && altMatch) {
            e.preventDefault();
            shortcut.action();
            return;
          }
        }
      }
    },
    [shortcuts, enabled, sequenceTimeout],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(sequenceTimerRef.current);
    };
  }, [handleKeyDown]);
}

// =============================================================================
// Hook: useShortcutsDialog
// =============================================================================

/**
 * Hook for managing the keyboard shortcuts help dialog.
 */
export function useShortcutsDialog() {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return { isOpen, open, close, toggle, setIsOpen };
}

// =============================================================================
// Default Global Shortcuts
// =============================================================================

/**
 * Creates standard global shortcuts.
 * Call this with your navigation functions to get pre-configured shortcuts.
 */
export function createGlobalShortcuts(handlers: {
  openSearch: () => void;
  openShortcutsHelp: () => void;
  goToDashboard: () => void;
  goToProjects: () => void;
  goToIssues: () => void;
  createIssue?: () => void;
  createProject?: () => void;
}): Shortcut[] {
  return [
    // Search & Help
    {
      id: 'open-search',
      keys: 'k',
      meta: true,
      action: handlers.openSearch,
      description: 'Open command palette',
      category: 'general',
    },
    {
      id: 'shortcuts-help',
      keys: '?',
      shift: true,
      action: handlers.openShortcutsHelp,
      description: 'Show keyboard shortcuts',
      category: 'general',
    },

    // Navigation (G then X sequences)
    {
      id: 'goto-dashboard',
      keys: ['g', 'd'],
      action: handlers.goToDashboard,
      description: 'Go to Dashboard',
      category: 'navigation',
    },
    {
      id: 'goto-projects',
      keys: ['g', 'p'],
      action: handlers.goToProjects,
      description: 'Go to Projects',
      category: 'navigation',
    },
    {
      id: 'goto-issues',
      keys: ['g', 'i'],
      action: handlers.goToIssues,
      description: 'Go to Issues',
      category: 'navigation',
    },

    // Actions
    ...(handlers.createIssue
      ? [
          {
            id: 'create-issue',
            keys: 'c',
            meta: true,
            shift: true,
            action: handlers.createIssue,
            description: 'Create new issue',
            category: 'actions' as const,
          },
        ]
      : []),
    ...(handlers.createProject
      ? [
          {
            id: 'create-project',
            keys: 'p',
            meta: true,
            shift: true,
            action: handlers.createProject,
            description: 'Create new project',
            category: 'actions' as const,
          },
        ]
      : []),
  ];
}

// =============================================================================
// Utility: Group shortcuts by category
// =============================================================================

export function groupShortcutsByCategory(shortcuts: Shortcut[]): ShortcutGroup[] {
  const categories = ['general', 'navigation', 'actions', 'editing'] as const;
  const categoryLabels: Record<string, string> = {
    general: 'General',
    navigation: 'Navigation',
    actions: 'Actions',
    editing: 'Editing',
  };

  return categories
    .map((category) => ({
      category: categoryLabels[category],
      shortcuts: shortcuts.filter((s) => (s.category || 'general') === category),
    }))
    .filter((group) => group.shortcuts.length > 0);
}
